import { BulkImportWizardFieldDefinition, BulkImportWizardEntityTypeDefinition, ImportEntityType } from '../../types/Import'
import { FuzzyMatchCandidate } from '../fuzzyMatch'
import { UserType, CommitteeTag } from '../../types/API'

export const EntityTypeDefinitions: BulkImportWizardEntityTypeDefinition[] = [
  {
    type: 'Company',
    aliases: [
      'company', 'companies', 'org', 'orgs', 'organisation', 'organisations',
      'organization', 'organizations', 'business', 'businesses', 'firm', 'firms',
      'company data', 'company list', 'company import'
    ]
  },
  {
    type: 'Individual',
    aliases: [
      'individual', 'individuals', 'board member', 'board members', 'boardmember',
      'boardmembers', 'member', 'members', 'participant', 'participants', 'person',
      'people', 'persons', 'director', 'directors', 'executive', 'executives',
      'staff', 'employee', 'employees', 'individual data', 'member list'
    ]
  },
  {
    type: 'Committee',
    aliases: [
      'committee', 'committees', 'group', 'groups', 'team', 'teams',
      'subcommittee', 'subcommittees', 'committee data', 'committee list'
    ]
  }
]

export const CompanyFieldDefinitions: BulkImportWizardFieldDefinition[] = [
  { key: 'name', label: 'Company Name', required: true, aliases: ['company name', 'org name', 'organisation', 'organization', 'business name', 'company'] },
  { key: 'registrationNumber', label: 'Registration Number', required: false, aliases: ['reg number', 'company number', 'crn', 'registration', 'company registration number', 'registration no'] },
  { key: 'phoneNumber', label: 'Phone Number', required: false, aliases: ['phone', 'telephone', 'contact number', 'tel', 'phone no', 'contact phone'] },
  { key: 'employeeCount', label: 'Employee Count', required: true, aliases: ['employees', 'staff count', 'headcount', 'number of employees', 'employee number', 'staff', 'workforce size'] },
  { key: 'sector', label: 'Sector', required: true, aliases: ['industry', 'business sector', 'industry sector', 'business type', 'field'] },
  { key: 'marketCap', label: 'Market Cap', required: true, aliases: ['market cap', 'market capitalization', 'market capitalisation', 'valuation', 'market value'] },
  { key: 'ownershipStructure', label: 'Ownership Structure', required: true, aliases: ['ownership', 'ownership type', 'company ownership'] },
  { key: 'address.line1', label: 'Address Line 1', required: false, aliases: ['address', 'street', 'address line 1', 'street address', 'address 1'] },
  { key: 'address.line2', label: 'Address Line 2', required: false, aliases: ['address line 2', 'address 2', 'suite', 'unit'] },
  { key: 'address.city', label: 'City', required: false, aliases: ['city', 'town', 'locality'] },
  { key: 'address.region', label: 'Region', required: false, aliases: ['region', 'state', 'province', 'county'] },
  { key: 'address.postalCode', label: 'Postal Code', required: false, aliases: ['postal code', 'postcode', 'zip', 'zip code', 'post code'] },
  { key: 'address.country', label: 'Country', required: true, aliases: ['country', 'nation'] }
]

export const IndividualFieldDefinitions: BulkImportWizardFieldDefinition[] = [
  { key: 'preferredName', label: 'Preferred Name', required: true, aliases: ['preferred name', 'chosen name', 'display name', 'nickname', 'known as', 'goes by'] },
  { key: 'email', label: 'Email', required: true, aliases: ['email', 'email address', 'e-mail', 'mail', 'contact email'] },
  { key: 'firstName', label: 'First Name', required: false, aliases: ['first name', 'given name', 'forename', 'christian name'] },
  { key: 'familyName', label: 'Family Name', required: false, aliases: ['family name', 'surname', 'last name', 'lastname'] },
  { key: 'title', label: 'Title', required: false, aliases: ['title', 'salutation', 'honorific', 'prefix'] },
  { key: 'role', label: 'Role', required: true, aliases: ['role', 'position', 'board role', 'board position', 'member type'] },
  { key: 'jobTitle', label: 'Job Title', required: false, aliases: ['job title', 'occupation', 'position title', 'job', 'professional title'] },
  { key: 'joinDate', label: 'Join Date', required: true, aliases: ['join date', 'start date', 'board join date', 'date joined', 'joining date', 'start'] },
  { key: 'gender', label: 'Gender', required: false, aliases: ['gender', 'sex'] },
  { key: 'religion', label: 'Religion', required: false, aliases: ['religion', 'faith', 'religious affiliation'] },
  { key: 'ethnicDesignation', label: 'Ethnic Background', required: false, aliases: ['ethnic background', 'ethnicity', 'ethnic designation', 'ethnic origin', 'background'] },
  { key: 'contactNumber', label: 'Contact Number', required: false, aliases: ['contact number', 'phone', 'telephone', 'mobile', 'cell'] },
  { key: 'dateOfBirth', label: 'Date of Birth', required: false, aliases: ['date of birth', 'dob', 'birth date', 'birthday'] }
]

export const CommitteeFieldDefinitions: BulkImportWizardFieldDefinition[] = [
  { key: 'name', label: 'Committee Name', required: true, aliases: ['committee name', 'name', 'committee'] },
  { key: 'type', label: 'Committee Type', required: false, aliases: ['committee type', 'type', 'category'] },
  { key: 'members', label: 'Members', required: false, aliases: ['members', 'committee members', 'member names', 'participants', 'member list'] }
]

export const EntityFieldDefinitionsMap: Record<ImportEntityType, BulkImportWizardFieldDefinition[]> = {
  Company: CompanyFieldDefinitions,
  Individual: IndividualFieldDefinitions,
  Committee: CommitteeFieldDefinitions
}

export function getFieldDefinitions (entityType: ImportEntityType): BulkImportWizardFieldDefinition[] {
  return EntityFieldDefinitionsMap[entityType]
}

export function getRequiredFields (entityType: ImportEntityType): BulkImportWizardFieldDefinition[] {
  return getFieldDefinitions(entityType).filter(f => f.required)
}

export const UserTypeCandidates: Array<FuzzyMatchCandidate<UserType>> = [
  { result: UserType.Chair, matchStrings: ['Chair', 'chair', 'chairman', 'chairwoman', 'chairperson', 'board chair'] },
  { result: UserType.DeputyChair, matchStrings: ['DeputyChair', 'deputy chair', 'deputy chairman', 'vice chairman', 'deputy chairperson'] },
  { result: UserType.ViceChair, matchStrings: ['ViceChair', 'vice chair', 'vice chairman', 'vice chairwoman', 'vice chairperson'] },
  { result: UserType.SID, matchStrings: ['SID', 'sid', 'senior independent director', 'senior independent', 'lead independent director'] },
  { result: UserType.NED, matchStrings: ['NED', 'ned', 'non-executive director', 'non executive director', 'non-exec', 'non exec director', 'independent director'] },
  { result: UserType.AssociateNED, matchStrings: ['AssociateNED', 'associate ned', 'associate non-executive director', 'associate non executive director'] },
  { result: UserType.OtherNonExecutive, matchStrings: ['OtherNonExecutive', 'other non-executive', 'other non executive', 'other ned'] },
  { result: UserType.MD, matchStrings: ['MD', 'md', 'managing director', 'managing dir'] },
  { result: UserType.CEO, matchStrings: ['CEO', 'ceo', 'chief executive officer', 'chief exec officer', 'chief executive', 'chief exec'] },
  { result: UserType.CFO, matchStrings: ['CFO', 'cfo', 'chief financial officer', 'chief finance officer', 'finance director', 'financial director'] },
  { result: UserType.CIO, matchStrings: ['CIO', 'cio', 'chief information officer', 'chief investment officer', 'information officer'] },
  { result: UserType.COO, matchStrings: ['COO', 'coo', 'chief operating officer', 'chief operations officer', 'operations director'] },
  { result: UserType.CMO, matchStrings: ['CMO', 'cmo', 'chief marketing officer', 'marketing director'] },
  { result: UserType.CPO, matchStrings: ['CPO', 'cpo', 'chief people officer', 'chief product officer', 'people director', 'hr director'] },
  { result: UserType.CSO, matchStrings: ['CSO', 'cso', 'chief strategy officer', 'chief security officer', 'strategy director'] },
  { result: UserType.CNO, matchStrings: ['CNO', 'cno', 'chief nursing officer', 'nursing director'] },
  { result: UserType.ShareholderRepresentative, matchStrings: ['ShareholderRepresentative', 'shareholder representative', 'shareholder rep', 'investor representative'] },
  { result: UserType.LabourRepresentative, matchStrings: ['LabourRepresentative', 'labour representative', 'labor representative', 'worker representative', 'employee representative'] },
  { result: UserType.OtherExecutive, matchStrings: ['OtherExecutive', 'other executive', 'executive', 'exec', 'other exec'] },
  { result: UserType.InternalInvestor, matchStrings: ['InternalInvestor', 'internal investor', 'inside investor'] },
  { result: UserType.InternalUnion, matchStrings: ['InternalUnion', 'internal union', 'union representative', 'union rep'] },
  { result: UserType.InternalStakeholder, matchStrings: ['InternalStakeholder', 'internal stakeholder', 'inside stakeholder'] },
  { result: UserType.ExternalInvestor, matchStrings: ['ExternalInvestor', 'external investor', 'outside investor'] },
  { result: UserType.ExternalUnion, matchStrings: ['ExternalUnion', 'external union', 'external union representative'] },
  { result: UserType.ExternalNGO, matchStrings: ['ExternalNGO', 'external ngo', 'ngo representative', 'ngo'] },
  { result: UserType.ExternalStakeholder, matchStrings: ['ExternalStakeholder', 'external stakeholder', 'outside stakeholder'] },
  { result: UserType.CompanySecretary, matchStrings: ['CompanySecretary', 'company secretary', 'secretary', 'corporate secretary', 'board secretary'] },
  { result: UserType.Auditor, matchStrings: ['Auditor', 'auditor', 'audit', 'external auditor'] },
  { result: UserType.RemunerationConsultant, matchStrings: ['RemunerationConsultant', 'remuneration consultant', 'compensation consultant', 'pay consultant', 'rem consultant'] },
  { result: UserType.ExecutiveSearchConsultant, matchStrings: ['ExecutiveSearchConsultant', 'executive search consultant', 'headhunter', 'search consultant', 'recruitment consultant'] }
]

export const CommitteeTagCandidates: Array<FuzzyMatchCandidate<CommitteeTag>> = [
  { result: CommitteeTag.Audit, matchStrings: ['Audit', 'audit', 'audit committee', 'audit and risk', 'audit & risk', 'risk and audit'] },
  { result: CommitteeTag.Remuneration, matchStrings: ['Remuneration', 'remuneration', 'remuneration committee', 'rem', 'rem committee', 'pay committee'] },
  { result: CommitteeTag.Compensation, matchStrings: ['Compensation', 'compensation', 'compensation committee', 'comp', 'comp committee'] },
  { result: CommitteeTag.Nomination, matchStrings: ['Nomination', 'nomination', 'nomination committee', 'nom', 'nominations', 'governance', 'nomination and governance', 'nom & gov'] },
  { result: CommitteeTag.Other, matchStrings: ['Other', 'other', 'general', 'miscellaneous', 'misc'] }
]

export const SectorAliases: Record<string, string[]> = {
  AdvancedElectronics: ['electronics', 'tech hardware', 'electronic components'],
  Aerospace: ['aerospace', 'defense', 'defence', 'aviation', 'military'],
  Agriculture: ['farming', 'agri', 'agribusiness', 'agricultural'],
  AutomotiveAssembly: ['automotive', 'auto', 'cars', 'vehicles', 'motor'],
  CapitalProjectInfrastructure: ['infrastructure', 'capital projects', 'construction projects'],
  Chemicals: ['chemical', 'petrochemical'],
  ConsumerPackagedGoods: ['cpg', 'consumer goods', 'fmcg', 'packaged goods'],
  ElectronicPowerGas: ['power', 'energy', 'electricity', 'natural gas', 'electric'],
  Utilities: ['utility', 'water', 'gas utility'],
  FinancialServices: ['finance', 'banking', 'financial', 'bank', 'fintech'],
  HealthcareSystems: ['healthcare', 'health', 'medical services', 'hospital'],
  IndustrialProductsConstruction: ['industrial', 'construction', 'building materials'],
  Insurance: ['insurer', 'underwriting'],
  ManufacturingEngineering: ['manufacturing', 'engineering', 'factory', 'production'],
  MetalsMining: ['metals', 'mining', 'steel', 'iron', 'ore'],
  OilGas: ['oil', 'gas', 'petroleum', 'energy', 'upstream', 'downstream'],
  PaperForestPackaging: ['paper', 'forest', 'packaging', 'pulp', 'timber'],
  Pharmaceuticals: ['pharma', 'pharmaceutical', 'medical products', 'drugs', 'biotech'],
  PrimarySecondaryEducation: ['education', 'school', 'schools', 'k-12', 'k12'],
  PrivateEquity: ['pe', 'private equity', 'investment', 'principal investors', 'vc', 'venture'],
  ProfessionalServices: ['consulting', 'professional', 'advisory', 'services'],
  PublicSector: ['government', 'public', 'civil service', 'state'],
  RealEstate: ['real estate', 'property', 'realty', 'commercial property'],
  RenewablesEnvironment: ['renewable', 'renewables', 'green energy', 'solar', 'wind', 'environment', 'sustainability'],
  Retail: ['retailer', 'shop', 'store', 'ecommerce', 'e-commerce'],
  SemiConductors: ['semiconductor', 'chips', 'chip', 'silicon'],
  SocialSector: ['social', 'nonprofit', 'non-profit', 'charity', 'ngo'],
  SoftwareComputing: ['software', 'computing', 'saas', 'it', 'tech', 'cloud'],
  TechnologyMedia: ['technology', 'media', 'telecom', 'telecommunications', 'tmt'],
  TravelTransportLogistics: ['travel', 'transport', 'logistics', 'shipping', 'freight', 'airline', 'aviation']
}

export const OwnershipStructureAliases: Record<string, string[]> = {
  FTSE100: ['ftse 100', 'ftse100', 'footsie 100'],
  FTSE250: ['ftse 250', 'ftse250', 'footsie 250'],
  FTSE350: ['ftse 350', 'ftse350'],
  FTSESmallCap: ['ftse small cap', 'small cap uk'],
  AIMMarket: ['aim', 'aim market', 'alternative investment market'],
  DAX40: ['dax', 'dax 40', 'german dax'],
  MDAX: ['m-dax', 'mid dax'],
  TecDAX: ['tec dax', 'tech dax'],
  SMICH20: ['smi', 'swiss market index'],
  SSIRT: ['six', 'swiss exchange'],
  EUROSTOXX50: ['euro stoxx', 'stoxx 50', 'eurostoxx'],
  BEL20: ['bel 20', 'brussels 20'],
  CAC40: ['cac', 'cac 40', 'paris'],
  DowJonesIndustrialAverage: ['dow jones', 'dow', 'djia'],
  'NASDAQ-100': ['nasdaq', 'nasdaq 100'],
  'S&P500': ['sp500', 's&p', 'sp 500', 'standard and poors'],
  HANGSENG: ['hang seng', 'hong kong'],
  Sensex: ['bse sensex', 'bombay'],
  Nifty50: ['nifty', 'nse nifty'],
  Russell3000: ['russell', 'russell 3000'],
  'PE-backed': ['pe backed', 'private equity', 'pe', 'private equity backed'],
  'VC-backed': ['vc backed', 'venture capital', 'vc', 'venture backed'],
  'Family Owned': ['family owned', 'family business', 'family-owned'],
  'Privately owned': ['privately owned', 'private', 'privately held'],
  NHS: ['national health service', 'health service'],
  'Central Government': ['central government', 'national government', 'federal'],
  'Local Government': ['local government', 'local authority', 'council', 'municipal'],
  'Higher Education': ['higher education', 'university', 'universities', 'hei'],
  'Privately-Run Public Services': ['public services', 'outsourced public'],
  ThirdSector: ['third sector', 'charity', 'nonprofit', 'non-profit', 'ngo', 'not for profit']
}

export const CountryAliases: Record<string, string[]> = {
  UK: ['united kingdom', 'great britain', 'britain', 'england', 'gb', 'gbr'],
  DE: ['germany', 'deutschland'],
  US: ['united states', 'united states of america', 'usa', 'america'],
  CH: ['switzerland', 'swiss', 'schweiz', 'suisse'],
  AR: ['argentina'],
  AU: ['australia', 'aus'],
  AT: ['austria', 'österreich'],
  BE: ['belgium', 'belgique', 'belgie'],
  BM: ['bermuda'],
  BR: ['brazil', 'brasil'],
  CA: ['canada'],
  CL: ['chile'],
  CN: ['china', 'prc', 'peoples republic of china'],
  CO: ['colombia'],
  HR: ['croatia', 'hrvatska'],
  CZ: ['czech republic', 'czechia', 'czech'],
  DK: ['denmark', 'danmark'],
  EG: ['egypt'],
  FI: ['finland', 'suomi'],
  FR: ['france'],
  GR: ['greece', 'hellas'],
  HU: ['hungary', 'magyarország'],
  IN: ['india'],
  ID: ['indonesia'],
  IT: ['italy', 'italia'],
  JP: ['japan', 'nippon'],
  JO: ['jordan'],
  KR: ['korea', 'south korea', 'republic of korea'],
  LU: ['luxembourg'],
  MY: ['malaysia'],
  MT: ['malta'],
  MX: ['mexico', 'méxico'],
  MD: ['moldova'],
  MA: ['morocco', 'maroc'],
  NL: ['netherlands', 'holland', 'the netherlands', 'nederland'],
  NO: ['norway', 'norge'],
  PL: ['poland', 'polska'],
  PT: ['portugal'],
  RU: ['russia', 'russian federation'],
  ZA: ['south africa', 'rsa'],
  ES: ['spain', 'españa', 'espana'],
  SE: ['sweden', 'sverige'],
  TJ: ['tajikistan'],
  TR: ['turkey', 'türkiye', 'turkiye'],
  AE: ['united arab emirates', 'uae', 'dubai', 'abu dhabi']
}
